import { useState, useCallback, useRef, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, setDoc, getDoc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, track } from '../services/firebase';
import { captureError } from '../services/sentry';
import { generateGameCode } from '../utils/gameCode';

export default function useGameState() {
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [turnNumber, setTurnNumber] = useState(1);
  const [currentScoreString, setCurrentScoreString] = useState('0');
  const [currentGameId, setCurrentGameId] = useState(null);
  const [view, setView] = useState('setup'); // 'setup' | 'photo' | 'lobby' | 'game' | 'victory'
  const [isFarkle, setIsFarkle] = useState(false);
  const [gamePhotoUrl, setGamePhotoUrl] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [gameCode, setGameCode] = useState(null);
  const [redemption, setRedemption] = useState(null);
  const [awaitingInheritChoice, setAwaitingInheritChoice] = useState(false);

  const unsubRef = useRef(null);
  const rewardTimerRef = useRef(null);
  const gameStartTimeRef = useRef(null);
  const lastActionRef = useRef(null);
  const choicePromptedForRef = useRef(null); // tracks which (gameId,playerIndex) pair we've already prompted

  const isPitchMode = true;
  const requiredTimeInSeconds = isPitchMode ? 45 : 60 * 60;

  // Reward state
  const [showReward, setShowReward] = useState(false);
  const [rewardPlayerName, setRewardPlayerName] = useState('');
  const [rewardTimeText, setRewardTimeText] = useState('');

  const detachListener = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
  }, []);

  const listenToGame = useCallback((gameId) => {
    detachListener();
    unsubRef.current = onSnapshot(doc(db, "games", gameId), (snapshot) => {
      if (snapshot.exists()) {
        const gameData = snapshot.data();
        if (gameData.status === 'finished') {
          detachListener();
          return;
        }
        setPlayers(gameData.players);
        setCurrentPlayerIndex(gameData.currentPlayerIndex);
        setTurnNumber(gameData.turnNumber);
        setCurrentScoreString('0');
        setIsFarkle(false);
        if (gameData.gamePhotoUrl) setGamePhotoUrl(gameData.gamePhotoUrl);
        if (gameData.gameCode) setGameCode(gameData.gameCode);

        const incomingRedemption = gameData.redemption || null;
        setRedemption(incomingRedemption);

        // Prompt inherit/fresh exactly once per player transition during redemption.
        if (incomingRedemption) {
          const key = `${gameId}:${gameData.currentPlayerIndex}`;
          if (choicePromptedForRef.current !== key) {
            choicePromptedForRef.current = key;
            setAwaitingInheritChoice(true);
          }
        } else {
          choicePromptedForRef.current = null;
          setAwaitingInheritChoice(false);
        }
      }
    });
  }, [detachListener]);

  const startGameTimer = useCallback(() => {
    gameStartTimeRef.current = Date.now();
    rewardTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - gameStartTimeRef.current) / 1000);
      if (elapsed >= requiredTimeInSeconds) {
        clearInterval(rewardTimerRef.current);
        setRewardPlayerName(prev => prev); // trigger from current state
        setShowReward(true);
      }
    }, 1000);
  }, [requiredTimeInSeconds]);

  // Update reward player name when showing reward
  useEffect(() => {
    if (showReward && players.length > 0) {
      setRewardPlayerName(players[currentPlayerIndex]?.name || 'Table');
      setRewardTimeText(isPitchMode ? '45 Seconds' : '1 Hour');
    }
  }, [showReward, players, currentPlayerIndex, isPitchMode]);

  const startGame = useCallback(async (gamePlayers, activePartner) => {
    try {
      // Generate unique game code
      let code;
      for (let i = 0; i < 5; i++) {
        code = generateGameCode();
        const existing = await getDoc(doc(db, 'gameCodes', code));
        if (!existing.exists()) break;
      }

      const docRef = await addDoc(collection(db, "games"), {
        venueId: activePartner,
        players: gamePlayers,
        playerIds: gamePlayers.map(p => p.id),
        currentPlayerIndex: 0,
        turnNumber: 1,
        targetScore: 10000,
        status: "active",
        gameCode: code,
        redemption: null,
        createdAt: new Date()
      });

      // Create lookup doc for spectators
      await setDoc(doc(db, 'gameCodes', code), {
        gameId: docRef.id,
        createdAt: new Date()
      });

      const gameId = docRef.id;
      setCurrentGameId(gameId);
      setGameCode(code);
      setPlayers(gamePlayers);
      localStorage.setItem('activeGameId', gameId);
      track('game_started', { mode: 'online', player_count: gamePlayers.length });
      setView('photo');
      // Return both ids so the caller can dispatch invites for remote players.
      return { gameId, gameCode: code };
    } catch (e) {
      console.error("Error creating game: ", e);
      captureError(e, { where: 'startGame', activePartner });
      alert("Make sure your Firebase keys are set up!");
      return null;
    }
  }, []);

  const startScratchGame = useCallback(async (gamePlayers, activePartner) => {
    try {
      const docRef = await addDoc(collection(db, "games"), {
        venueId: activePartner,
        players: gamePlayers,
        playerIds: gamePlayers.map(p => p.id),
        currentPlayerIndex: 0,
        turnNumber: 1,
        targetScore: 10000,
        status: "active",
        isScratch: true,
        redemption: null,
        createdAt: new Date()
      });

      const gameId = docRef.id;
      setCurrentGameId(gameId);
      setPlayers(gamePlayers);
      localStorage.setItem('activeGameId', gameId);
      listenToGame(gameId);
      setView('game');
      startGameTimer();
      track('game_started', { mode: 'scratch', player_count: gamePlayers.length });
      return gameId;
    } catch (e) {
      console.error("Error creating scratch game: ", e);
      captureError(e, { where: 'startScratchGame', activePartner });
      alert("Could not create game. Check your connection.");
      return null;
    }
  }, [listenToGame, startGameTimer]);

  const uploadGamePhoto = useCallback(async (file) => {
    const photoPath = `game-photos/${currentGameId}_${file.name}`;
    const storageRef = ref(storage, photoPath);
    const uploadResult = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(uploadResult.ref);

    await updateDoc(doc(db, "games", currentGameId), { gamePhotoUrl: url });
    setGamePhotoUrl(url);
    listenToGame(currentGameId);
    setView('lobby');
  }, [currentGameId, listenToGame]);

  const skipGamePhoto = useCallback(() => {
    listenToGame(currentGameId);
    setView('lobby');
  }, [currentGameId, listenToGame]);

  const startFromLobby = useCallback(() => {
    setView('game');
    startGameTimer();
  }, [startGameTimer]);

  const handleNumKey = useCallback((num) => {
    if (awaitingInheritChoice) return;
    setIsFarkle(false);
    setCurrentScoreString(prev => prev === '0' ? num : prev + num);
  }, [awaitingInheritChoice]);

  const handleDelete = useCallback(() => {
    if (awaitingInheritChoice) return;
    setCurrentScoreString(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
  }, [awaitingInheritChoice]);

  const handleQuickAdd = useCallback((amount) => {
    if (awaitingInheritChoice) return;
    setIsFarkle(false);
    setCurrentScoreString(prev => {
      const current = parseInt(prev, 10) || 0;
      return (current + amount).toString();
    });
  }, [awaitingInheritChoice]);

  const chooseInherit = useCallback(() => {
    if (!redemption) return;
    setCurrentScoreString(String(redemption.currentTarget));
    setIsFarkle(false);
    setAwaitingInheritChoice(false);
  }, [redemption]);

  const chooseFresh = useCallback(() => {
    setCurrentScoreString('0');
    setIsFarkle(false);
    setAwaitingInheritChoice(false);
  }, []);

  function buildRemainingOrder(playersArr, startIndex) {
    const ids = [];
    for (let i = 1; i < playersArr.length; i++) {
      const idx = (startIndex + i) % playersArr.length;
      ids.push(playersArr[idx].id);
    }
    return ids;
  }

  const bankScore = useCallback(async () => {
    if (!currentGameId) return;
    if (awaitingInheritChoice) return;
    const score = parseInt(currentScoreString, 10) || 0;
    const player = players[currentPlayerIndex];

    // Save snapshot for undo
    lastActionRef.current = {
      players: JSON.parse(JSON.stringify(players)),
      currentPlayerIndex,
      turnNumber,
      redemption: redemption ? JSON.parse(JSON.stringify(redemption)) : null,
    };
    setCanUndo(true);

    const updatedPlayers = JSON.parse(JSON.stringify(players));

    // Redemption-round bank: score REPLACES the player's total.
    if (redemption) {
      updatedPlayers[currentPlayerIndex].score = score;
      updatedPlayers[currentPlayerIndex].lastTurn = `Final: ${score.toLocaleString()}`;

      let newTarget = redemption.currentTarget;
      let newLeaderId = redemption.currentLeaderId;
      if (score > redemption.currentTarget) {
        newTarget = score;
        newLeaderId = player.id;
      }

      const newRemaining = redemption.remainingPlayerIds.filter(id => id !== player.id);

      if (newRemaining.length === 0) {
        // Redemption round complete — leader wins.
        const winnerIndex = updatedPlayers.findIndex(p => p.id === newLeaderId);
        const winnerPlayer = updatedPlayers[winnerIndex];
        detachListener();
        localStorage.removeItem('activeGameId');
        setPlayers(updatedPlayers);
        setCurrentPlayerIndex(winnerIndex);
        setRedemption(null);
        setAwaitingInheritChoice(false);
        setView('victory');

        const gameRef = doc(db, "games", currentGameId);
        updateDoc(gameRef, {
          players: updatedPlayers,
          status: 'finished',
          winner: { id: winnerPlayer.id, name: winnerPlayer.name },
          redemption: null,
        }).catch(e => console.error("Error saving game result:", e));
        track('game_completed', { ending: 'redemption', player_count: updatedPlayers.length });
        return;
      }

      const nextPlayerId = newRemaining[0];
      const nextPlayerIndex = updatedPlayers.findIndex(p => p.id === nextPlayerId);
      const nextTurnNumber = nextPlayerIndex === 0 ? turnNumber + 1 : turnNumber;

      const gameRef = doc(db, "games", currentGameId);
      await updateDoc(gameRef, {
        players: updatedPlayers,
        currentPlayerIndex: nextPlayerIndex,
        turnNumber: nextTurnNumber,
        redemption: {
          triggeredById: redemption.triggeredById,
          currentTarget: newTarget,
          currentLeaderId: newLeaderId,
          remainingPlayerIds: newRemaining,
        },
      });
      return;
    }

    // Normal bank (redemption not active)
    const newTotalScore = player.score + score;
    updatedPlayers[currentPlayerIndex].score = newTotalScore;
    updatedPlayers[currentPlayerIndex].lastTurn = `+${score.toLocaleString()}`;

    if (newTotalScore >= 10000) {
      const remainingPlayerIds = buildRemainingOrder(players, currentPlayerIndex);

      // Solo game edge case: no one else to play a redemption turn, immediate win.
      if (remainingPlayerIds.length === 0) {
        detachListener();
        localStorage.removeItem('activeGameId');
        setPlayers(updatedPlayers);
        setView('victory');

        const gameRef = doc(db, "games", currentGameId);
        updateDoc(gameRef, {
          players: updatedPlayers,
          status: 'finished',
          winner: { id: player.id, name: player.name }
        }).catch(e => console.error("Error saving game result:", e));
        track('game_completed', { ending: 'solo_win', player_count: updatedPlayers.length });
        return;
      }

      // Trigger redemption round.
      const nextPlayerId = remainingPlayerIds[0];
      const nextPlayerIndex = players.findIndex(p => p.id === nextPlayerId);
      const nextTurnNumber = nextPlayerIndex === 0 ? turnNumber + 1 : turnNumber;

      updatedPlayers[currentPlayerIndex].lastTurn = `+${score.toLocaleString()} — Redemption!`;

      const gameRef = doc(db, "games", currentGameId);
      await updateDoc(gameRef, {
        players: updatedPlayers,
        currentPlayerIndex: nextPlayerIndex,
        turnNumber: nextTurnNumber,
        redemption: {
          triggeredById: player.id,
          currentTarget: newTotalScore,
          currentLeaderId: player.id,
          remainingPlayerIds,
        },
      });
      return;
    }

    const nextPlayerIndex = (currentPlayerIndex + 1) % players.length;
    const nextTurnNumber = nextPlayerIndex === 0 ? turnNumber + 1 : turnNumber;

    const gameRef = doc(db, "games", currentGameId);
    await updateDoc(gameRef, {
      players: updatedPlayers,
      currentPlayerIndex: nextPlayerIndex,
      turnNumber: nextTurnNumber
    });
  }, [currentGameId, currentScoreString, players, currentPlayerIndex, turnNumber, detachListener, redemption, awaitingInheritChoice]);

  const handleFarkle = useCallback(async () => {
    if (!currentGameId) return;
    if (awaitingInheritChoice) return;

    // Save snapshot for undo
    lastActionRef.current = {
      players: JSON.parse(JSON.stringify(players)),
      currentPlayerIndex,
      turnNumber,
      redemption: redemption ? JSON.parse(JSON.stringify(redemption)) : null,
    };
    setCanUndo(true);

    const updatedPlayers = JSON.parse(JSON.stringify(players));
    const player = players[currentPlayerIndex];

    setIsFarkle(true);
    setCurrentScoreString('0');

    // Redemption Farkle: the player's game score is unchanged, they just
    // forfeit this attempt and the round rolls on.
    if (redemption) {
      updatedPlayers[currentPlayerIndex].lastTurn = 'NO DICE! (Redemption blown)';

      const newRemaining = redemption.remainingPlayerIds.filter(id => id !== player.id);

      if (newRemaining.length === 0) {
        const winnerIndex = updatedPlayers.findIndex(p => p.id === redemption.currentLeaderId);
        const winnerPlayer = updatedPlayers[winnerIndex];
        detachListener();
        localStorage.removeItem('activeGameId');

        const gameRef = doc(db, "games", currentGameId);
        setTimeout(async () => {
          setPlayers(updatedPlayers);
          setCurrentPlayerIndex(winnerIndex);
          setRedemption(null);
          setAwaitingInheritChoice(false);
          setView('victory');
          await updateDoc(gameRef, {
            players: updatedPlayers,
            status: 'finished',
            winner: { id: winnerPlayer.id, name: winnerPlayer.name },
            redemption: null,
          });
        }, 1200);
        return;
      }

      const nextPlayerId = newRemaining[0];
      const nextPlayerIndex = updatedPlayers.findIndex(p => p.id === nextPlayerId);
      const nextTurnNumber = nextPlayerIndex === 0 ? turnNumber + 1 : turnNumber;

      const gameRef = doc(db, "games", currentGameId);
      setTimeout(async () => {
        await updateDoc(gameRef, {
          players: updatedPlayers,
          currentPlayerIndex: nextPlayerIndex,
          turnNumber: nextTurnNumber,
          redemption: {
            ...redemption,
            remainingPlayerIds: newRemaining,
          },
        });
      }, 1200);
      return;
    }

    // Normal Farkle
    updatedPlayers[currentPlayerIndex].lastTurn = 'NO DICE!';
    const nextPlayerIndex = (currentPlayerIndex + 1) % players.length;
    const nextTurnNumber = nextPlayerIndex === 0 ? turnNumber + 1 : turnNumber;

    const gameRef = doc(db, "games", currentGameId);
    setTimeout(async () => {
      await updateDoc(gameRef, {
        players: updatedPlayers,
        currentPlayerIndex: nextPlayerIndex,
        turnNumber: nextTurnNumber
      });
    }, 1200);
  }, [currentGameId, players, currentPlayerIndex, turnNumber, redemption, awaitingInheritChoice, detachListener]);

  const undoLastAction = useCallback(async () => {
    if (!lastActionRef.current || !currentGameId) return;
    const snapshot = lastActionRef.current;

    const gameRef = doc(db, "games", currentGameId);
    await updateDoc(gameRef, {
      players: snapshot.players,
      currentPlayerIndex: snapshot.currentPlayerIndex,
      turnNumber: snapshot.turnNumber,
      redemption: snapshot.redemption || null,
    });

    lastActionRef.current = null;
    setCanUndo(false);
    setCurrentScoreString('0');
    setIsFarkle(false);
    // Let the snapshot listener re-evaluate whether to show inherit/fresh.
    choicePromptedForRef.current = null;
  }, [currentGameId]);

  const saveToVault = useCallback(async (file, trashTalk, activePartner, venueName) => {
    const winner = players[currentPlayerIndex];
    const filePath = `vault/${currentGameId || Date.now()}/${file.name}`;
    const storageRef = ref(storage, filePath);
    const uploadResult = await uploadBytes(storageRef, file);
    const imageUrl = await getDownloadURL(uploadResult.ref);

    await addDoc(collection(db, "vault"), {
      winnerName: winner.name,
      winnerId: winner.id,
      gameId: currentGameId,
      venueId: activePartner,
      venueName: venueName,
      imageUrl: imageUrl,
      trashTalk: trashTalk,
      createdAt: new Date()
    });
    track('vault_saved', { has_trash_talk: !!trashTalk });
  }, [players, currentPlayerIndex, currentGameId]);

  const returnToLobby = useCallback(() => {
    detachListener();
    localStorage.removeItem('activeGameId');
    setCurrentGameId(null);
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setTurnNumber(1);
    setCurrentScoreString('0');
    setIsFarkle(false);
    setGamePhotoUrl(null);
    setGameCode(null);
    setCanUndo(false);
    setRedemption(null);
    setAwaitingInheritChoice(false);
    choicePromptedForRef.current = null;
    lastActionRef.current = null;
    setView('setup');
    if (rewardTimerRef.current) {
      clearInterval(rewardTimerRef.current);
      gameStartTimeRef.current = null;
    }
  }, [detachListener]);

  const resumeActiveGame = useCallback(() => {
    const activeGameId = localStorage.getItem('activeGameId');
    if (activeGameId) {
      setCurrentGameId(activeGameId);
      listenToGame(activeGameId);
      setView('game');
      startGameTimer();
      return true;
    }
    return false;
  }, [listenToGame, startGameTimer]);

  // Visibility change handler for cost savings
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden && unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      } else if (!document.hidden && currentGameId && !unsubRef.current) {
        listenToGame(currentGameId);
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentGameId, listenToGame]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      detachListener();
      if (rewardTimerRef.current) clearInterval(rewardTimerRef.current);
    };
  }, [detachListener]);

  const currentScore = parseInt(currentScoreString, 10) || 0;

  return {
    players,
    currentPlayerIndex,
    turnNumber,
    currentScore,
    currentScoreString,
    currentGameId,
    view,
    isFarkle,
    showReward,
    rewardPlayerName,
    rewardTimeText,
    setShowReward,
    startGame,
    startScratchGame,
    uploadGamePhoto,
    skipGamePhoto,
    startFromLobby,
    gamePhotoUrl,
    gameCode,
    handleNumKey,
    handleDelete,
    handleQuickAdd,
    bankScore,
    handleFarkle,
    undoLastAction,
    canUndo,
    saveToVault,
    returnToLobby,
    resumeActiveGame,
    redemption,
    awaitingInheritChoice,
    chooseInherit,
    chooseFresh,
  };
}
